<template>
  <v-app>
    <!-- Simple Header -->
    <v-app-bar app color="white" elevation="1" height="60">
      <v-container class="d-flex align-center pa-0">
        <img src="../../assets/vastrosa-logo.png" alt="Vastrosa" height="40" />
        <v-spacer></v-spacer>
      </v-container>
    </v-app-bar>

    <v-main class="grey lighten-5">
      <!-- Loading State -->
      <v-container v-if="isLoading" fluid class="fill-height">
        <v-row justify="center" align="center" style="min-height: 60vh">
          <v-col cols="12" class="text-center">
            <v-progress-circular
              indeterminate
              color="primary"
              size="70"
              width="7"
            ></v-progress-circular>
            <p class="mt-6 text-h6 font-weight-medium">Loading product details...</p>
          </v-col>
        </v-row>
      </v-container>

      <!-- Error State -->
      <v-container v-else-if="error" fluid class="fill-height">
        <v-row justify="center" align="center" style="min-height: 60vh">
          <v-col cols="12" md="6" class="text-center">
            <v-icon color="error" size="120">mdi-alert-circle-outline</v-icon>
            <h2 class="mt-6 text-h4 font-weight-bold">{{ error }}</h2>
            <v-btn
              color="primary"
              class="mt-6"
              x-large
              rounded
              elevation="0"
              @click="loadVariantDetails"
            >
              <v-icon left>mdi-refresh</v-icon>
              Try Again
            </v-btn>
          </v-col>
        </v-row>
      </v-container>

      <!-- Product Details -->
      <v-container v-else-if="variant" class="py-6 white" style="max-width: 1400px">
        <!-- Breadcrumb -->
        <v-breadcrumbs :items="breadcrumbs" class="pa-0 mb-4">
          <template v-slot:divider>
            <v-icon small>mdi-chevron-right</v-icon>
          </template>
        </v-breadcrumbs>

        <v-row>
          <!-- Left Section - Image Gallery -->
          <v-col cols="12" md="5">
            <!-- Main Image -->
            <v-card flat class="mb-3">
              <v-img
                :src="selectedImage || (variant.product_variant_images && variant.product_variant_images.length > 0 ? `${mediaUrl}${variant.product_variant_images[0].image_path}` : '')"
                height="450"
                contain
                class="white"
              >
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate color="grey lighten-2"></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
            </v-card>

            <!-- Thumbnail Images -->
            <v-row dense v-if="allImages.length > 1">
              <v-col
                cols="3"
                v-for="(image, index) in allImages"
                :key="index"
              >
                <v-card
                  flat
                  outlined
                  class="thumbnail-card"
                  :class="{ 'active-thumbnail': selectedImage === image }"
                  @click="selectedImage = image"
                >
                  <v-img
                    :src="image"
                    height="80"
                    contain
                    class="white"
                  ></v-img>
                </v-card>
              </v-col>
            </v-row>

            <!-- Product Description (Below Images) -->
            <v-card flat class="mt-6" v-if="product.description">
              <v-card-title class="text-h6 font-weight-bold pa-0 mb-3">
                Product Description
              </v-card-title>
              <v-card-text class="pa-0">
                <p class="text-body-2 grey--text text--darken-2">
                  {{ product.description }}
                </p>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Right Section - Product Info -->
          <v-col cols="12" md="7">
            <!-- Store/Company Name -->
            <div class="text-caption primary--text mb-2" v-if="product.company">
              Visit the {{ product.company.name }} Store
            </div>

            <!-- Product Title -->
            <h1 class="text-h5 font-weight-bold mb-3">
              {{ product.name }} || {{ variant.color }} {{ variant.size }}
            </h1>

            <!-- Ratings -->
            <div class="d-flex align-center mb-4" v-if="product.ratings">
              <v-rating
                :value="parseFloat(product.ratings)"
                color="amber"
                background-color="grey lighten-2"
                dense
                half-increments
                readonly
                size="18"
              ></v-rating>
              <span class="ml-2 text-body-2">{{ product.ratings }}</span>
            </div>

            <v-divider class="mb-4"></v-divider>

            <!-- Price Section -->
            <div class="mb-4">
              <!-- Discount Badge -->
              <v-chip
                v-if="discountPercent > 0"
                color="error"
                text-color="white"
                small
                class="mb-2"
              >
                {{ discountPercent }}% OFF
              </v-chip>

              <!-- Limited Stock Badge -->
              <v-chip
                v-if="!variant.in_stock"
                color="error"
                text-color="white"
                small
                class="mb-2 ml-2"
              >
                Limited Stock
              </v-chip>

              <!-- Pricing Display -->
              <div class="d-flex align-center mb-2">
                <span class="error--text text-h6 font-weight-bold mr-2" v-if="discountPercent > 0">
                  -{{ discountPercent }}%
                </span>
                <span class="text-h4 font-weight-bold black--text">
                  ₹{{ formatPrice(sellingPrice) }}
                </span>
              </div>

              <!-- MRP - only when there is an actual discount -->
              <div class="text-body-2 grey--text" v-if="savingsAmount > 0">
                M.R.P.: <span class="text-decoration-line-through">₹{{ formatPrice(mrpPrice) }}</span>
              </div>

              <!-- Savings -->
              <div class="text-body-1 success--text font-weight-medium mt-1" v-if="savingsAmount > 0">
                You save: ₹{{ formatPrice(savingsAmount) }}
                <span v-if="discountPercent > 0">({{ discountPercent }}% off)</span>
              </div>

              <div class="text-caption grey--text mt-1">
                Inclusive of all taxes
              </div>
            </div>

            <!-- Color Selection -->
            <div class="mb-4" v-if="variant.color && availableColors.length > 0">
              <div class="text-subtitle-2 font-weight-bold mb-2">
                Colour: <span class="font-weight-regular">{{ variant.color }}</span>
              </div>
              <v-row dense>
                <v-col
                  cols="auto"
                  v-for="color in availableColors"
                  :key="color.id"
                >
                  <v-card
                    flat
                    outlined
                    class="color-card pa-2"
                    :class="{ 'selected-color-card': color.id === variant.id }"
                    @click="selectColor(color)"
                    width="80"
                  >
                    <v-img
                      :src="getColorImage(color)"
                      height="60"
                      contain
                      class="white"
                    >
                      <template v-slot:placeholder>
                        <v-row class="fill-height ma-0" align="center" justify="center">
                          <v-icon color="grey lighten-2">mdi-image</v-icon>
                        </v-row>
                      </template>
                    </v-img>
                    <div class="text-center mt-1">
                      <div class="text-caption font-weight-bold">
                        ₹{{ formatPrice(priceOf(color).selling) }}
                      </div>
                      <div
                        class="text-caption grey--text text-decoration-line-through"
                        v-if="priceOf(color).savings > 0"
                      >
                        ₹{{ formatPrice(priceOf(color).mrp) }}
                      </div>
                      <div class="text-caption error--text" v-if="priceOf(color).percent > 0">
                        -{{ priceOf(color).percent }}%
                      </div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <!-- Size Selection -->
            <div class="mb-4" v-if="variant.size && availableSizes.length > 0">
              <div class="d-flex align-items-center mb-2">
                <div class="text-subtitle-2 font-weight-bold">
                  Size: <span class="font-weight-regular">{{ variant.size }}</span>
                </div>
                <v-spacer></v-spacer>
                <v-btn text x-small color="primary" class="text-capitalize">
                  Size Guide
                </v-btn>
              </div>
              <!-- <div class="d-flex flex-wrap">
                <v-btn
                  v-for="size in availableSizes"
                  :key="size.id"
                  :outlined="size.id !== variant.id"
                  :color="size.id === variant.id ? 'black' : ''"
                  :disabled="!size.in_stock"
                  @click="selectSize(size)"
                  class="mr-2 mb-2 size-btn"
                  :class="{ 'selected-size': size.id === variant.id }"
                >
                  {{ size.size }}
                  <span v-if="!size.in_stock" class="ml-1 text-caption">(Out of Stock)</span>
                </v-btn>
              </div> -->
            </div>

            <v-divider class="my-4"></v-divider>

            <!-- Product QR Code -->
            <div class="mb-4" v-if="variant.id">
              <div class="text-subtitle-2 font-weight-bold mb-2">
                Product QR Code
              </div>
              <div class="d-flex align-center">
                <div class="qr-box pa-2 mr-4">
                  <qrcode-vue
                    :text="variantUrl"
                    :size="120"
                    error-level="H"
                    ref="variantQr"
                  ></qrcode-vue>
                </div>
                <div>
                  <div
                    class="text-body-2 font-weight-bold qr-code-text"
                    v-if="variant.qr_code_data"
                  >
                    {{ variant.qr_code_data }}
                  </div>
                  <div class="text-caption grey--text mb-2">
                    Scan to open this product
                  </div>
                  <v-btn
                    small
                    outlined
                    color="primary"
                    :loading="isDownloadingQr"
                    @click="downloadQr"
                  >
                    <v-icon left small>mdi-download</v-icon>
                    Download QR
                  </v-btn>
                </div>
              </div>
            </div>

            <v-divider class="my-4"></v-divider>

            <!-- Features/Benefits -->
            <v-row dense class="mb-4">
              <!-- Paid Delivery - Only show if delivery_type is 1 -->
              <v-col cols="12" sm="6" md="4" class="mb-3" v-if="product.delivery_type === 1">
                <div class="d-flex align-center">
                  <v-icon color="orange" class="mr-2">mdi-truck-delivery</v-icon>
                  <div>
                    <div class="text-body-2 font-weight-bold">Delivery Charges</div>
                    <div class="text-caption grey--text">
                      ₹{{ formatPrice(product.delivery_amount) }}
                    </div>
                  </div>
                </div>
              </v-col>

              <!-- Return & Exchange -->
              <v-col cols="12" sm="6" md="4" class="mb-3">
                <div class="d-flex align-center">
                  <v-icon color="blue" class="mr-2">mdi-autorenew</v-icon>
                  <div>
                    <div class="text-body-2 font-weight-bold">7 Days Return & Exchange</div>
                    <div class="text-caption grey--text">Easy return within 7 days</div>
                  </div>
                </div>
              </v-col>

              <!-- Authentic Products -->
              <v-col cols="12" sm="6" md="4" class="mb-3">
                <div class="d-flex align-center">
                  <v-icon color="purple" class="mr-2">mdi-check-decagram</v-icon>
                  <div>
                    <div class="text-body-2 font-weight-bold">100% Authentic Products</div>
                    <div class="text-caption grey--text">Guaranteed quality</div>
                  </div>
                </div>
              </v-col>
            </v-row>

            <!-- Flash Deal Alert -->
            <v-alert
              v-if="product.is_flash_deal"
              type="warning"
              text
              dense
              class="mb-4"
            >
              <div class="d-flex align-center">
                <v-icon left>mdi-lightning-bolt</v-icon>
                <strong>Flash Deal!</strong>
                <span v-if="product.end_date" class="ml-2">
                  Ends: {{ product.end_date | niceDate }}
                </span>
              </div>
            </v-alert>

            <!-- Stock Status -->
            <!-- <div class="mb-4">
              <v-chip
                :color="variant.in_stock ? 'success' : 'error'"
                text-color="white"
                small
              >
                <v-icon left small>
                  {{ variant.in_stock ? 'mdi-check-circle' : 'mdi-close-circle' }}
                </v-icon>
                {{ variant.stock_status }}
              </v-chip>
            </div> -->
          </v-col>
        </v-row>

        <!-- Related Variants Section (Horizontal Scroll) -->
        <!-- <v-row class="mt-8" v-if="product.variants && product.variants.length > 1">
          <v-col cols="12">
            <h3 class="text-h6 font-weight-bold mb-4">
              {{ product.company ? product.company.name : 'Vastrosa' }} Pvt. Ltd. - All rights reserved.
            </h3>
            <div class="variants-scroll-container">
              <div class="d-flex" style="overflow-x: auto; gap: 16px;">
                <v-card
                  v-for="relatedVariant in product.variants"
                  :key="relatedVariant.id"
                  flat
                  outlined
                  class="variant-thumbnail-card"
                  :class="{ 'active-variant': relatedVariant.id === variant.id }"
                  @click="selectVariant(relatedVariant)"
                  width="200"
                >
                  <v-img
                    :src="getVariantImage(relatedVariant)"
                    height="200"
                    contain
                    class="white"
                  >
                    <template v-slot:placeholder>
                      <v-row class="fill-height ma-0" align="center" justify="center">
                        <v-icon large color="grey lighten-2">mdi-image</v-icon>
                      </v-row>
                    </template>
                  </v-img>
                  <v-card-text class="pa-2">
                    <div class="text-body-2 font-weight-bold text-truncate">
                      {{ product.name }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ relatedVariant.color }} | {{ relatedVariant.size }}
                    </div>
                    <div class="d-flex align-center mt-1">
                      <span class="text-body-2 font-weight-bold">₹{{ formatPrice(priceOf(relatedVariant).selling) }}</span>
                      <span
                        class="text-caption grey--text text-decoration-line-through ml-2"
                        v-if="priceOf(relatedVariant).savings > 0"
                      >
                        ₹{{ formatPrice(priceOf(relatedVariant).mrp) }}
                      </span>
                    </div>
                    <div class="text-caption error--text" v-if="priceOf(relatedVariant).percent > 0">
                      -{{ priceOf(relatedVariant).percent }}%
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </div>
          </v-col>
        </v-row> -->
      </v-container>

      <!-- Snackbar -->
      <v-snackbar
        v-model="snackbar"
        :color="snackbarColor"
        timeout="3000"
        top
        right
      >
        <v-icon left>{{ snackbarIcon }}</v-icon>
        {{ snackbarMessage }}
        <template v-slot:action="{ attrs }">
          <v-btn icon small v-bind="attrs" @click="snackbar = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
    </v-main>

    <!-- Footer -->
    <v-footer color="grey darken-3" dark class="pa-4">
      <v-container>
        <v-row>
          <v-col cols="12" class="text-center">
            <p class="mb-0">
              <strong>{{ product.company ? product.company.name : 'Vastrosa' }}</strong>
              &copy; {{ new Date().getFullYear() }}. All rights reserved.
            </p>
          </v-col>
        </v-row>
      </v-container>
    </v-footer>
  </v-app>
</template>

<script>
import axios from "axios";
import QrcodeVue from "vue-qrcode-component";
import { priceOf, downloadVariantQrLabel } from "@/utils/variantQr";

export default {
  name: "PublicVariantDetails",
  components: { QrcodeVue },
  data() {
    return {
      variant: null,
      product: {},
      isLoading: false,
      error: null,
      selectedImage: null,
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      snackbarIcon: "mdi-check-circle",
      availableColors: [],
      availableSizes: [],
      isDownloadingQr: false,
    };
  },
  computed: {
    allImages() {
      const images = [];
      if (this.variant && this.variant.product_variant_images && this.variant.product_variant_images.length > 0) {
        this.variant.product_variant_images.forEach((img) => {
          if (img.image_path) {
            images.push(`${this.mediaUrl}${img.image_path}`);
          }
        });
      }
      return images;
    },
    breadcrumbs() {
      const items = [
        { text: 'Home', disabled: false, href: '#' },
      ];

      if (this.product.category && this.product.category.parent_category) {
        items.push({
          text: this.product.category.parent_category.name,
          disabled: false,
        });
      }

      if (this.product.category) {
        items.push({
          text: this.product.category.name,
          disabled: false,
        });
      }

      if (this.product.name) {
        items.push({
          text: this.product.name,
          disabled: true,
        });
      }

      return items;
    },
    sellingPrice() {
      return this.priceOf(this.variant).selling;
    },
    mrpPrice() {
      return this.priceOf(this.variant).mrp;
    },
    savingsAmount() {
      return this.priceOf(this.variant).savings;
    },
    discountPercent() {
      return this.priceOf(this.variant).percent;
    },
    variantUrl() {
      // Same URL the printed QR codes point at (encrypted variant id)
      return `${window.location.origin}/variant/${this.$route.params.variantId}`;
    },
  },
  mounted() {
    this.loadVariantDetails();
  },
  methods: {
    priceOf,

    async downloadQr() {
      this.isDownloadingQr = true;

      try {
        const qrCanvas = this.$refs.variantQr.$el.querySelector("canvas");

        // Same composite label as the admin QR codes page
        await downloadVariantQrLabel({
          qrSource: qrCanvas,
          variant: { ...this.variant, product: this.product },
        });
      } catch (error) {
        console.error("Error downloading QR code:", error);
      } finally {
        this.isDownloadingQr = false;
      }
    },

    async loadVariantDetails() {
      this.isLoading = true;
      this.error = null;

      const encryptedVariantId = this.$route.params.variantId;

      if (!encryptedVariantId) {
        this.error = "Invalid variant ID";
        this.isLoading = false;
        return;
      }

      try {
        const response = await axios.get(`/public/product-variants/${encryptedVariantId}`);

        this.variant = response.data.data;
        this.product = response.data.data.product || {};

        // Only show the scanned variant - don't show other color/size options
        // This ensures users only see the variant from the QR code they scanned
        this.availableColors = [this.variant];
        this.availableSizes = [this.variant];

        // Set first image as selected
        if (this.allImages.length > 0) {
          this.selectedImage = this.allImages[0];
        }
      } catch (error) {
        console.error("Error loading variant details:", error);
        this.error = "Failed to load product details. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },
    formatPrice(price) {
      return new Intl.NumberFormat('en-IN').format(price);
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    getColorImage(color) {
      if (color.product_variant_images && color.product_variant_images.length > 0) {
        return `${this.mediaUrl}${color.product_variant_images[0].image_path}`;
      }
      if (color.id === this.variant.id && this.allImages.length > 0) {
        return this.allImages[0];
      }
      return '';
    },
    getVariantImage(variant) {
      if (variant.product_variant_images && variant.product_variant_images.length > 0) {
        return `${this.mediaUrl}${variant.product_variant_images[0].image_path}`;
      }
      return '';
    },
    selectColor(color) {
      if (color.id !== this.variant.id) {
        this.$router.push(`/public/product-variants/${color.id}`);
        window.location.reload();
      }
    },
    selectSize(size) {
      if (size.id !== this.variant.id) {
        this.$router.push(`/public/product-variants/${size.id}`);
        window.location.reload();
      }
    },
    selectVariant(variant) {
      if (variant.id !== this.variant.id) {
        this.$router.push(`/public/product-variants/${variant.id}`);
        window.location.reload();
      }
    },
    showSnackbar(message, color, icon) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbarIcon = icon || 'mdi-check-circle';
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
/* Product QR Code */
.qr-box {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  display: inline-flex;
}

.qr-code-text {
  font-family: "Courier New", monospace;
  letter-spacing: 0.5px;
}

/* Thumbnail Styles */
.thumbnail-card {
  cursor: pointer;
  border: 2px solid #ddd;
  transition: all 0.2s;
}

.thumbnail-card:hover {
  border-color: #ff9800;
}

.active-thumbnail {
  border-color: #ff9800 !important;
  box-shadow: 0 0 8px rgba(255, 152, 0, 0.5);
}

/* Color Card Styles */
.color-card {
  cursor: pointer;
  border: 2px solid #ddd !important;
  transition: all 0.2s;
}

.color-card:hover {
  border-color: #000 !important;
}

.selected-color-card {
  border-color: #000 !important;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.3);
}

/* Size Button Styles */
.size-btn {
  min-width: 60px !important;
  height: 40px !important;
  border: 1px solid #ddd !important;
}

.selected-size {
  background-color: black !important;
  color: white !important;
}

/* Variant Thumbnail Cards */
.variant-thumbnail-card {
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;
  border: 2px solid #ddd;
}

.variant-thumbnail-card:hover {
  border-color: #ff9800;
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.active-variant {
  border-color: #000 !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* Horizontal Scroll */
.variants-scroll-container {
  overflow-x: auto;
  padding-bottom: 16px;
}

.variants-scroll-container::-webkit-scrollbar {
  height: 8px;
}

.variants-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.variants-scroll-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.variants-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Responsive */
@media (max-width: 960px) {
  .color-card {
    width: 70px !important;
  }

  .variant-thumbnail-card {
    width: 160px !important;
  }
}

@media (max-width: 600px) {
  .size-btn {
    min-width: 50px !important;
    height: 36px !important;
    font-size: 0.875rem !important;
  }

  .variants-scroll-container {
    margin: 0 -16px;
    padding: 0 16px 16px;
  }
}
</style>
